import { Link } from '@tanstack/react-router'
import { ExternalLink } from 'lucide-react'
import { Button, Chip, Drawer, Tooltip, VendorCostBreakdown } from '@/components/ui'
import { classifyMargin } from '@/lib/utils/margin-thresholds'
import { odooUrl } from '@/lib/utils/odoo-url'
import type { CustomerOrderHistoryEntry, CustomerPricingRow } from '../api/types'
import { CostProvenanceHint } from './CostProvenanceHint'

export interface CustomerOrderHistoryDrawerProps {
  /** The full row, already in memory from the page's own query result —
   * this drawer is purely presentational and issues no network request
   * of its own (the ops route already returns `history` inline). */
  row: CustomerPricingRow | null
  onClose: () => void
  unit: 'kg' | 'lb'
}

export function CustomerOrderHistoryDrawer({ row, onClose, unit }: CustomerOrderHistoryDrawerProps) {
  const history = row?.history ?? []

  return (
    <Drawer
      open={row !== null}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      size="wide"
      title={row?.customer_name ?? ''}
      meta={
        row ? (
          <>
            <span>{row.orders_count_in_window} orders in window</span>
            <span className="opacity-40">·</span>
            <span>Last order {fmtDate(row.last_order_date)}</span>
          </>
        ) : undefined
      }
      headerActions={
        row ? (
          <div className="flex items-center gap-3">
            <Link
              to="/pricelists"
              search={{ partner: row.customer_id }}
              className="inline-flex items-center gap-1 text-xs text-info hover:underline"
            >
              View pricelist <ExternalLink size={12} />
            </Link>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => window.open(odooUrl('res.partner', row.customer_id), '_blank')}
            >
              Open customer in Odoo
              <ExternalLink size={12} />
            </Button>
          </div>
        ) : undefined
      }
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {row === null ? null : (
        <div className="flex flex-col gap-5 p-4">
          <MarginTrendChart history={history} />
          <OrderHistoryTable history={history} unit={unit} />
        </div>
      )}
    </Drawer>
  )
}

function MarginTrendChart({ history }: { history: CustomerOrderHistoryEntry[] }) {
  if (history.length < 2) {
    return (
      <section>
        <SectionLabel>Margin % over time</SectionLabel>
        <p className="text-role-metadata text-tertiary">
          Not enough orders in this window to show a trend.
        </p>
      </section>
    )
  }

  const width = 560
  const height = 140
  const padX = 12
  const padY = 20

  const values = history.map((h) => h.true_margin_pct ?? 0)
  const dataMin = Math.min(...values, 0)
  const dataMax = Math.max(...values, 0)
  const range = dataMax - dataMin || 1

  const xFor = (i: number) => (i / (history.length - 1)) * (width - padX * 2) + padX
  const yFor = (v: number) => height - padY - ((v - dataMin) / range) * (height - padY * 2)
  const zeroY = yFor(0)

  const points = history.map((h, i) => [xFor(i), yFor(h.true_margin_pct ?? 0)] as const)
  const path = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
  const [lastX, lastY] = points[points.length - 1]
  const lastValue = history[history.length - 1].true_margin_pct

  return (
    <section>
      <SectionLabel>Margin % over time</SectionLabel>
      <svg width={width} height={height} role="img" aria-label="Margin percent over time">
        {/* Zero/breakeven reference line — hairline, recessive */}
        <line
          x1={padX}
          y1={zeroY}
          x2={width - padX}
          y2={zeroY}
          stroke="var(--color-border-subtle)"
          strokeWidth={1}
        />
        <text x={padX} y={zeroY - 4} className="fill-tertiary text-2xs">
          0%
        </text>

        <path d={path} fill="none" stroke="var(--color-accent)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />

        {history.map((h, i) => {
          const [x, y] = points[i]
          return (
            <Tooltip
              key={h.invoice_id ?? i}
              label={`${fmtDate(h.invoice_date)} — margin ${fmtPct(h.true_margin_pct)}, price ${fmtMoney(h.price_unit)}`}
            >
              <circle cx={x} cy={y} r={4} fill="var(--color-accent)" stroke="var(--color-surface)" strokeWidth={2} />
            </Tooltip>
          )
        })}

        {/* Direct end-label — current value */}
        <text x={lastX} y={lastY - 8} textAnchor="end" className="fill-primary text-xs font-semibold">
          {fmtPct(lastValue)}
        </text>
      </svg>
      <div className="flex justify-between text-role-metadata text-tertiary">
        <span>{fmtDate(history[0].invoice_date)}</span>
        <span>{fmtDate(history[history.length - 1].invoice_date)}</span>
      </div>
    </section>
  )
}

function OrderHistoryTable({ history, unit }: { history: CustomerOrderHistoryEntry[]; unit: 'kg' | 'lb' }) {
  return (
    <section>
      <SectionLabel>Order history ({history.length})</SectionLabel>
      {history.length === 0 ? (
        <EmptyRow>No orders in this window</EmptyRow>
      ) : (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border-default">
              <Th>Date</Th>
              <Th right>Qty</Th>
              <Th right>Price / {unit}</Th>
              <Th right>Cost / {unit}</Th>
              <Th right>Vendor cost</Th>
              <Th right>Margin %</Th>
              <Th>Invoice</Th>
              <Th>Sale order</Th>
            </tr>
          </thead>
          <tbody>
            {[...history].reverse().map((h, i) => (
              <tr key={h.invoice_id ?? i} className="border-b border-border-subtle">
                <Td>{fmtDate(h.invoice_date)}</Td>
                <Td right>{fmtQty(h.qty)}</Td>
                <Td right>{fmtMoney(unit === 'kg' ? h.price_per_kg : h.price_per_lb)}</Td>
                <Td right>
                  <span className="inline-flex items-center justify-end gap-1">
                    {fmtMoney(unit === 'kg' ? h.cost_per_kg : h.cost_per_lb)}
                    {h.cost_predates_first_po ? <CostProvenanceHint /> : null}
                  </span>
                </Td>
                <Td right>
                  <VendorCostBreakdown
                    breakdown={h.vendor_cost_breakdown}
                    blendedUnitCost={h.vendor_cost_blended_per_unit}
                  />
                </Td>
                <Td right>
                  <MarginCell value={h.true_margin_pct} />
                </Td>
                <Td>
                  {h.invoice_id ? (
                    <a
                      href={odooUrl('account.move', h.invoice_id)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-link hover:underline"
                    >
                      {h.invoice_name || `#${h.invoice_id}`}
                    </a>
                  ) : (
                    '—'
                  )}
                </Td>
                <Td>
                  {h.sale_orders.length === 0
                    ? '—'
                    : h.sale_orders.map((so, idx) => (
                        <span key={so.id}>
                          {idx > 0 ? ', ' : ''}
                          <a
                            href={odooUrl('sale.order', so.id)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-link hover:underline"
                          >
                            {so.name}
                          </a>
                        </span>
                      ))}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function MarginCell({ value }: { value: number | null }) {
  if (value === null) return <span className="text-tertiary">—</span>
  const bucket = classifyMargin(value)
  // Chip has no 'negative' variant — MarginIndicator paints
  // negative the same as danger (both red), so fold it in here too.
  const tone = bucket === 'negative' ? 'danger' : bucket
  return (
    <Chip size="xs" variant={tone}>
      {value.toFixed(1)}%
    </Chip>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="mb-2 border-b border-border-subtle pb-1 text-role-section-label">{children}</div>
}

function Th({ children, right }: { children?: React.ReactNode; right?: boolean }) {
  return (
    <th className={'py-1.5 pr-2 text-role-section-label ' + (right ? 'text-right' : 'text-left')}>{children}</th>
  )
}

function Td({ children, right }: { children?: React.ReactNode; right?: boolean }) {
  return (
    <td className={'py-1.5 pr-2 text-sm text-primary ' + (right ? 'text-right' : 'text-left')}>{children}</td>
  )
}

function EmptyRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-sm border border-border-subtle bg-surface-sunk p-3 text-xs italic text-tertiary">
      {children}
    </div>
  )
}

function fmtQty(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })
}

function fmtMoney(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 4 })
}

function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return `${n.toFixed(1)}%`
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—'
  return new Date(d).toLocaleDateString()
}
